import ELK from 'elkjs/lib/elk.bundled.js';
import { Edge, MarkerType, Node, Position } from 'reactflow';
import {
  isValidEdgeShape,
  isValidNodeShape,
  isWorkflowDefinitionShape,
  isWorkflowNodeType,
  ParsedEdgeShape,
  ParsedNodeShape,
  ParsedWorkflowDefinition,
  WorkflowDefinition,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
  WorkflowInsertionPoint,
  WorkflowNodeType,
  type WorkflowNodeTypeValue,
  WorkflowStepCatalog,
} from './workflow-definition';
import { getOpenOutputs } from './workflow-definition-edits';
import { withoutFoldedSteps } from './workflow-folding';

/**
 * What a step card needs to draw itself before it reads the step: its title and colours. The
 * step's data, summary and problems come from `WorkflowEditorContext` by `nodeId`, so typing in a
 * field re-renders the card without rebuilding the graph or re-running the layout.
 */
export interface WorkflowNodeDisplayData {
  title: string;
  nodeType: string;
  nodeId: string;
  /** The catalog doesn't describe `nodeType`: drawn as "Unknown step", read-only, saved untouched. */
  isUnknown: boolean;
  /** Laid out left to right: connections enter on the left and leave on the right. */
  horizontal?: boolean;
  icon?: string;
  iconColor?: string;
  headerColor?: string;
  accentColor?: string;
  detailsLabel?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/** The + at the end of a path, where a step can be added. An empty `if` output uses its chip. */
export interface WorkflowAddStepNodeData {
  insertion: Extract<WorkflowInsertionPoint, { kind: 'output' }>;
  /** Laid out left to right: the + sits at the left, where the connection arrives. */
  horizontal?: boolean;
}

/** A connection between two steps; `editable` puts the + on it. */
export interface WorkflowEdgeData {
  definitionEdgeId: string;
  editable: boolean;
}

export type WorkflowGraphNode = Node<WorkflowNodeDisplayData | WorkflowAddStepNodeData>;
/** Step connections carry `WorkflowEdgeData`; the lines to add-step targets carry none. */
export type WorkflowGraphEdge = Edge<WorkflowEdgeData | undefined>;

interface ReactFlowGraph {
  nodes: WorkflowGraphNode[];
  edges: WorkflowGraphEdge[];
}

export interface BuildWorkflowGraphOptions {
  catalog: WorkflowStepCatalog;
  isDarkMode?: boolean;
  layoutDirection?: WorkflowLayoutDirection;
  /** A draft the user can edit: connections get a +, and open outputs get an add-step target. */
  editable?: boolean;
  /** Folded `if` outputs (see `outputKey`); the steps only they lead to are left out. */
  folded?: ReadonlySet<string>;
}

const COLUMN_COUNT = 3;
const COLUMN_WIDTH = 320;
const ROW_HEIGHT = 180;
const ELK_NODE_WIDTH = 280;
const ELK_NODE_HEIGHT = 160;
const ELK_ADD_STEP_HEIGHT = 56;

export const STEP_NODE_TYPE = 'workflowNode';
export const ADD_STEP_NODE_TYPE = 'workflowAddStep';
export const STEP_EDGE_TYPE = 'workflowEdge';

export enum WorkflowLayoutDirection {
  Down = 'DOWN',
  Right = 'RIGHT',
}

const elk = new ELK();
const getElkOptions = (direction: WorkflowLayoutDirection) => ({
  'elk.algorithm': 'layered',
  'elk.direction': direction,
  'elk.layered.spacing.nodeNodeBetweenLayers': '150',
  'elk.spacing.nodeNode': '100',
  'elk.layered.spacing.edgeNodeBetweenLayers': '40',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.edgeRouting': 'ORTHOGONAL',
});

const UNKNOWN_STEP_TITLE = 'Unknown step';

export const formatNodeTitle = (type: string, catalog: WorkflowStepCatalog): string =>
  catalog.byType.get(type)?.label ?? UNKNOWN_STEP_TITLE;

interface NodeVisualConfig {
  icon: string;
  iconColor?: string;
  headerColor: string;
  accentColor: string;
  detailsLabel?: string;
}

interface NodeVisualThemeConfig {
  headerColor: string;
  accentColor: string;
}

interface NodeVisualConfigEntry {
  icon: string;
  iconColor?: string;
  detailsLabel?: string;
  light: NodeVisualThemeConfig;
  dark: NodeVisualThemeConfig;
}

const DEFAULT_VISUAL_CONFIG: NodeVisualConfigEntry = {
  icon: 'category',
  detailsLabel: 'Expand details',
  light: {
    headerColor: '#EDF1F6',
    accentColor: '#F7F9FC',
  },
  dark: {
    headerColor: '#3B4A5A',
    accentColor: '#2B3847',
  },
};

/** Creating a conversation looks the same whatever the channel. */
const CREATE_CONVERSATION_VISUAL_CONFIG: NodeVisualConfigEntry = {
  icon: 'call',
  light: {
    headerColor: '#E8F5E9',
    accentColor: '#F4FBF5',
  },
  dark: {
    headerColor: '#1E4620',
    accentColor: '#153016',
  },
};

/** Each step type the portal knows has its own look; the record makes a new type add one. */
const NODE_VISUAL_CONFIG: Record<WorkflowNodeTypeValue, NodeVisualConfigEntry> = {
  [WorkflowNodeType.Trigger]: {
    icon: 'play_circle',
    light: {
      headerColor: '#E1F5FE',
      accentColor: '#F0FBFF',
    },
    dark: {
      headerColor: '#1B4F5A',
      accentColor: '#0F3C47',
    },
  },
  [WorkflowNodeType.If]: {
    icon: 'schema',
    light: {
      headerColor: '#F0E8FF',
      accentColor: '#F9F5FF',
    },
    dark: {
      headerColor: '#3D2E5A',
      accentColor: '#2B1E42',
    },
  },
  [WorkflowNodeType.Say]: {
    icon: 'campaign',
    light: {
      headerColor: '#E6ECFF',
      accentColor: '#EFF2FF',
    },
    dark: {
      headerColor: '#2C3A5A',
      accentColor: '#1F2A42',
    },
  },
  [WorkflowNodeType.CreateConversation]: CREATE_CONVERSATION_VISUAL_CONFIG,
  [WorkflowNodeType.CreateConversationForPhoneCall]: CREATE_CONVERSATION_VISUAL_CONFIG,
  [WorkflowNodeType.Play]: {
    icon: 'play_arrow',
    light: {
      headerColor: '#FFF3E0',
      accentColor: '#FFF8E7',
    },
    dark: {
      headerColor: '#4A3F1F',
      accentColor: '#362B14',
    },
  },
  [WorkflowNodeType.SetVariables]: {
    icon: 'dataset',
    light: {
      headerColor: '#EDE7F6',
      accentColor: '#F5F2FB',
    },
    dark: {
      headerColor: '#3B3350',
      accentColor: '#2A2438',
    },
  },
  [WorkflowNodeType.EnqueueForAgent]: {
    icon: 'support_agent',
    light: {
      headerColor: '#E1F5FE',
      accentColor: '#F0FBFF',
    },
    dark: {
      headerColor: '#1B4F5A',
      accentColor: '#0F3C47',
    },
  },
  [WorkflowNodeType.ConnectCallTo]: {
    icon: 'phone_forwarded',
    light: {
      headerColor: '#FFF8E1',
      accentColor: '#FFFCEB',
    },
    dark: {
      headerColor: '#4A4520',
      accentColor: '#362F14',
    },
  },
  [WorkflowNodeType.Voicemail]: {
    icon: 'voicemail',
    light: { headerColor: '#FCE4EC', accentColor: '#FDF2F6' },
    dark: { headerColor: '#4A2533', accentColor: '#361A25' },
  },
  [WorkflowNodeType.Callback]: {
    icon: 'phone_callback',
    light: { headerColor: '#FCE4EC', accentColor: '#FDF2F6' },
    dark: { headerColor: '#4A2533', accentColor: '#361A25' },
  },
  [WorkflowNodeType.CheckTimeAgainstSchedule]: {
    icon: 'schedule',
    light: { headerColor: '#E0F2F1', accentColor: '#F2FAF9' },
    dark: { headerColor: '#1D4440', accentColor: '#14312E' },
  },
  [WorkflowNodeType.CloseSmsConversation]: {
    icon: 'speaker_notes_off',
    light: { headerColor: '#ECEFF1', accentColor: '#F6F7F8' },
    dark: { headerColor: '#37474F', accentColor: '#263238' },
  },
};

/** The look of a step type; one the portal doesn't list gets the default look. */
const visualConfigFor = (type: string): NodeVisualConfigEntry =>
  isWorkflowNodeType(type) ? NODE_VISUAL_CONFIG[type] : DEFAULT_VISUAL_CONFIG;

const UNKNOWN_VISUAL_CONFIG: NodeVisualConfigEntry = {
  icon: 'help_outline',
  light: { headerColor: '#EEEEEE', accentColor: '#F7F7F7' },
  dark: { headerColor: '#3A3A3A', accentColor: '#2A2A2A' },
};

const getNodeVisualConfig = (
  type: string,
  isUnknown: boolean,
  isDarkMode = false,
): NodeVisualConfig => {
  const visualConfig = isUnknown ? UNKNOWN_VISUAL_CONFIG : visualConfigFor(type);
  const { light, dark, ...sharedConfig } = visualConfig;
  const themeConfig = isDarkMode ? dark : light;

  return {
    ...sharedConfig,
    ...themeConfig,
  };
};

/** The icon a step type is drawn with, for the + menu. */
export const getStepIcon = (type: string): string => visualConfigFor(type).icon;

/** Where connections meet a card: top and bottom down the page, left and right across it. */
const handlePositions = (direction: WorkflowLayoutDirection) =>
  direction === WorkflowLayoutDirection.Right
    ? { targetPosition: Position.Left, sourcePosition: Position.Right }
    : { targetPosition: Position.Top, sourcePosition: Position.Bottom };

// It applies automatic layout to the given nodes and edges using ELK
const applyElkLayout = async (
  nodes: WorkflowGraphNode[],
  edges: Edge[],
  direction: WorkflowLayoutDirection,
): Promise<WorkflowGraphNode[]> => {
  if (!nodes.length) {
    return nodes;
  }

  const graph = {
    id: 'workflow-root',
    layoutOptions: getElkOptions(direction),
    children: nodes.map((node) => ({
      id: node.id,
      width: ELK_NODE_WIDTH,
      height: node.type === ADD_STEP_NODE_TYPE ? ELK_ADD_STEP_HEIGHT : ELK_NODE_HEIGHT,
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  const layoutedGraph = await elk.layout(graph);
  const nodePositions = new Map(
    layoutedGraph.children?.map((child) => [child.id, child]) ?? [],
  );

  return nodes.map((node) => {
    const layoutNode = nodePositions.get(node.id);
    if (!layoutNode) {
      return node;
    }

    return {
      ...node,
      position: {
        x: layoutNode.x ?? node.position.x ?? 0,
        y: layoutNode.y ?? node.position.y ?? 0,
      },
      ...handlePositions(direction),
    };
  });
};

/** A true output is drawn green; every other connection in the outline colour. */
const edgeStyle = (condition: boolean | undefined, isDarkMode: boolean) => {
  if (condition === true) {
    return { stroke: isDarkMode ? '#66bb6a' : '#2e7d32', strokeWidth: 2 };
  }
  return { stroke: isDarkMode ? '#899294' : '#6f797a', strokeWidth: 1.5 };
};

const sourceHandleFor = (condition: boolean | undefined) => {
  if (typeof condition !== 'boolean') return undefined;
  return condition ? 'if-true' : 'if-false';
};

const HIDDEN_MARKER = {
  type: MarkerType.ArrowClosed,
  width: 0,
  height: 0,
  color: 'transparent',
} as const;

export const buildReactFlowGraph = async (
  definition: WorkflowDefinition,
  {
    catalog,
    isDarkMode = false,
    layoutDirection = WorkflowLayoutDirection.Down,
    editable = false,
    folded = new Set(),
  }: BuildWorkflowGraphOptions,
): Promise<ReactFlowGraph> => {
  const shown = withoutFoldedSteps(definition, folded);
  const shownIds = new Set(shown.nodes.map(({ id }) => id));

  const stepNodes: Node<WorkflowNodeDisplayData>[] = shown.nodes.map((node, index) => {
    const isUnknown = !catalog.byType.has(node.type);
    const visualConfig = getNodeVisualConfig(node.type, isUnknown, isDarkMode);
    const position =
      node.position ??
      ({
        x: (index % COLUMN_COUNT) * COLUMN_WIDTH,
        y: Math.floor(index / COLUMN_COUNT) * ROW_HEIGHT,
      } as const);

    return {
      id: node.id,
      type: STEP_NODE_TYPE,
      data: {
        title: formatNodeTitle(node.type, catalog),
        nodeType: node.type,
        nodeId: node.id,
        isUnknown,
        icon: visualConfig.icon,
        iconColor: visualConfig.iconColor,
        headerColor: visualConfig.headerColor,
        accentColor: visualConfig.accentColor,
        detailsLabel: visualConfig.detailsLabel,
        horizontal: layoutDirection === WorkflowLayoutDirection.Right,
      },
      position,
      ...handlePositions(layoutDirection),
      draggable: true,
      zIndex: index + 1,
    };
  });

  const stepEdges: WorkflowGraphEdge[] = shown.edges.map((edge, index) => {
    const condition = edge.data?.condition;
    const definitionEdgeId = edge.id ?? `edge-${index}`;
    let conditionSuffix = '';
    if (typeof condition === 'boolean') {
      conditionSuffix = condition ? '-true' : '-false';
    }

    return {
      id: `${definitionEdgeId}${conditionSuffix}`,
      source: edge.source,
      target: edge.target,
      type: STEP_EDGE_TYPE,
      sourceHandle: sourceHandleFor(condition),
      animated: false,
      markerEnd: HIDDEN_MARKER,
      style: edgeStyle(condition, isDarkMode),
      data: { definitionEdgeId, editable },
    };
  });

  // In a draft, every output with nothing after it gets a target to add a step there — except an
  // `if` output, which the card's own "+ True" / "+ False" chip adds to.
  const openOutputs = editable
    ? getOpenOutputs(definition, catalog).filter(
        ({ sourceId, condition }) => shownIds.has(sourceId) && typeof condition !== 'boolean',
      )
    : [];
  const addStepNodes: Node<WorkflowAddStepNodeData>[] = openOutputs.map((insertion) => ({
    id: `add:${insertion.sourceId}:${insertion.condition ?? 'next'}`,
    type: ADD_STEP_NODE_TYPE,
    data: { insertion, horizontal: layoutDirection === WorkflowLayoutDirection.Right },
    position: { x: 0, y: 0 },
    draggable: false,
    selectable: false,
  }));
  const addStepEdges: WorkflowGraphEdge[] = addStepNodes.map((node) => ({
    id: `to-${node.id}`,
    source: node.data.insertion.sourceId,
    target: node.id,
    type: 'smoothstep',
    sourceHandle: sourceHandleFor(node.data.insertion.condition),
    markerEnd: HIDDEN_MARKER,
    style: { ...edgeStyle(node.data.insertion.condition, isDarkMode), opacity: 0.5 },
  }));

  const nodes: WorkflowGraphNode[] = [...stepNodes, ...addStepNodes];
  const edges = [...stepEdges, ...addStepEdges];

  const layoutedNodes = await applyElkLayout(nodes, edges, layoutDirection).catch((error) => {
    console.warn(
      'Failed to layout workflow graph with ELK. Falling back to default layout.',
      error,
    );
    return nodes;
  });

  // Across the page, a + sits level with the step it follows: same top, and the card draws itself
  // as tall as that step (see AddStepNode), so the connection runs straight across.
  const horizontal = layoutDirection === WorkflowLayoutDirection.Right;
  const topById = new Map(layoutedNodes.map(({ id, position }) => [id, position.y]));
  const alignedNodes = horizontal
    ? layoutedNodes.map((node) => {
        if (node.type !== ADD_STEP_NODE_TYPE) return node;
        const sourceTop = topById.get(
          (node.data as WorkflowAddStepNodeData).insertion.sourceId,
        );
        return sourceTop === undefined
          ? node
          : { ...node, position: { ...node.position, y: sourceTop } };
      })
    : layoutedNodes;

  return { nodes: alignedNodes, edges };
};

/**
 * What the layout depends on — step ids, types and stored positions, and the connections — as a
 * JSON string. Field values are left out, so editing a message doesn't move the canvas, and the
 * string parses back into a definition the graph can be built from.
 */
export const getWorkflowGraphStructureKey = (definition: WorkflowDefinition): string =>
  JSON.stringify({
    nodes: definition.nodes.map(({ id, type, position }) => ({ id, type, position })),
    edges: definition.edges.map(({ id, source, target, data }) => ({
      id,
      source,
      target,
      data,
    })),
  } satisfies WorkflowDefinition);

export const parseWorkflowDefinition = (
  rawDefinition?: string | null,
): ParsedWorkflowDefinition => {
  if (!rawDefinition) {
    return { definition: null, error: null };
  }

  try {
    const parsed = JSON.parse(rawDefinition) as unknown;
    if (!isWorkflowDefinitionShape(parsed)) {
      throw new Error('Definition must include nodes and edges arrays.');
    }

    const nodes: WorkflowDefinitionNode[] = parsed.nodes
      .filter(isValidNodeShape)
      .map((node: ParsedNodeShape) => {
        // An unknown type is kept as stored: it renders as unknown and is saved back untouched.
        return {
          id: node.id,
          type: node.type,
          data: node.data ?? {},
          position:
            typeof node.position?.x === 'number' && typeof node.position?.y === 'number'
              ? node.position
              : undefined,
        };
      });

    const edges: WorkflowDefinitionEdge[] = parsed.edges
      .filter(isValidEdgeShape)
      .map((edge: ParsedEdgeShape, index: number) => ({
        id: edge.id ?? `edge-${index}`,
        source: edge.source,
        target: edge.target,
        data: edge.data,
      }));

    if (!nodes.length) {
      throw new Error('Definition does not contain any nodes.');
    }

    return { definition: { nodes, edges }, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to parse definition.';
    return { definition: null, error: message };
  }
};
